import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Mail, Send, Users, FileEdit, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CrmCommunications = () => {
  const [comms, setComms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('log');
  // Send
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendName, setSendName] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendBody, setSendBody] = useState('');
  const [sendType, setSendType] = useState('email');
  // Bulk
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkBody, setBulkBody] = useState('');
  // Template editor
  const [editOpen, setEditOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [commRes, tplRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/communications`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/email-templates`, { withCredentials: true })
      ]);
      setComms(commRes.data);
      setTemplates(tplRes.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const sendComm = async () => {
    if (!sendEmail || !sendBody) { toast.error('Email and body required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/communications/send`, {
        recipient_email: sendEmail, recipient_name: sendName, type: sendType, subject: sendSubject, body: sendBody
      }, { withCredentials: true });
      toast.success('Message sent (mocked)');
      setSendOpen(false);
      setSendEmail(''); setSendName(''); setSendSubject(''); setSendBody('');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const sendBulk = async () => {
    if (!bulkBody) { toast.error('Body required'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/admin/communications/bulk`, {
        recipients: 'all', subject: bulkSubject, body: bulkBody, type: 'email'
      }, { withCredentials: true });
      toast.success(res.data.message);
      setBulkOpen(false);
      setBulkSubject(''); setBulkBody('');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const saveTemplate = async () => {
    if (!editTemplate) return;
    try {
      await axios.post(`${API_URL}/api/admin/email-templates`, editTemplate, { withCredentials: true });
      toast.success('Template saved');
      setEditOpen(false);
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const applyTemplate = (tpl) => {
    setSendSubject(tpl.subject);
    setSendBody(tpl.body);
    setSendOpen(true);
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Communications</h1>
          <p className="text-[#6B6B7A]">Email templates, messaging & history (MOCKED)</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setSendOpen(true)} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="send-message-btn">
            <Send className="w-4 h-4 mr-2" /> Send Message
          </Button>
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="bulk-message-btn">
            <Users className="w-4 h-4 mr-2" /> Bulk Message
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 grid w-full max-w-sm grid-cols-2">
          <TabsTrigger value="log" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white">
            <Mail className="w-4 h-4 mr-2" /> Message Log
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white">
            <FileEdit className="w-4 h-4 mr-2" /> Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="log">
          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comms.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6B6B7A]">No messages sent yet</TableCell></TableRow>
                ) : comms.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {c.type}
                      </span>
                      {c.is_bulk && <span className="ml-1 px-2 py-0.5 bg-[#FFD166]/20 text-[#B8860B] rounded-full text-xs">bulk</span>}
                      {c.is_auto_reminder && <span className="ml-1 px-2 py-0.5 bg-[#06D6A0]/20 text-[#06D6A0] rounded-full text-xs">auto</span>}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{c.recipient_name || c.recipient_email}</p>
                      <p className="text-xs text-[#6B6B7A]">{c.recipient_email}</p>
                    </TableCell>
                    <TableCell className="max-w-[250px] truncate text-sm">{c.subject}</TableCell>
                    <TableCell><span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">{c.status}</span></TableCell>
                    <TableCell className="text-xs text-[#6B6B7A]">{new Date(c.sent_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(tpl => (
              <div key={tpl.template_id} className="bg-white rounded-2xl border border-[#E8E5DF] p-6 card-hover">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-[#1E1E24]">{tpl.name}</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { setEditTemplate({...tpl}); setEditOpen(true); }} className="h-7 w-7 p-0" data-testid={`edit-tpl-${tpl.template_id}`}>
                      <FileEdit className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => applyTemplate(tpl)} className="h-7 text-xs text-[#FF3B6B]" data-testid={`use-tpl-${tpl.template_id}`}>
                      Use
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-[#6B6B7A] mb-2">Subject: {tpl.subject}</p>
                <p className="text-xs text-[#6B6B7A] whitespace-pre-line line-clamp-3">{tpl.body}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Send Dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Send Message</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setSendType('email')}
                className={`rounded-full text-xs ${sendType === 'email' ? 'bg-[#FF3B6B] text-white' : 'bg-gray-100 text-gray-700'}`}>Email</Button>
              <Button size="sm" onClick={() => setSendType('sms')}
                className={`rounded-full text-xs ${sendType === 'sms' ? 'bg-[#FF3B6B] text-white' : 'bg-gray-100 text-gray-700'}`}>SMS</Button>
            </div>
            <div>
              <Label className="font-semibold">Recipient Email *</Label>
              <Input value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="customer@email.com" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="send-email" />
            </div>
            <div>
              <Label className="font-semibold">Name</Label>
              <Input value={sendName} onChange={e => setSendName(e.target.value)} placeholder="Customer name" className="mt-1 rounded-xl border-[#E8E5DF]" />
            </div>
            {sendType === 'email' && (
              <div>
                <Label className="font-semibold">Subject</Label>
                <Input value={sendSubject} onChange={e => setSendSubject(e.target.value)} placeholder="Email subject" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="send-subject" />
              </div>
            )}
            <div>
              <Label className="font-semibold">Message *</Label>
              <Textarea value={sendBody} onChange={e => setSendBody(e.target.value)} rows={5} placeholder="Your message..." className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="send-body" />
            </div>
            <Button onClick={sendComm} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-send-btn">
              <Send className="w-4 h-4 mr-2" /> Send (Mocked)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Bulk Message</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B6B7A] mb-4">Send to all customers in your database.</p>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Subject</Label>
              <Input value={bulkSubject} onChange={e => setBulkSubject(e.target.value)} placeholder="Subject line" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="bulk-subject" />
            </div>
            <div>
              <Label className="font-semibold">Message *</Label>
              <Textarea value={bulkBody} onChange={e => setBulkBody(e.target.value)} rows={5} placeholder="Message body..." className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="bulk-body" />
            </div>
            <Button onClick={sendBulk} className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="confirm-bulk-btn">
              <Users className="w-4 h-4 mr-2" /> Send to All Customers (Mocked)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Edit Template</DialogTitle></DialogHeader>
          {editTemplate && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Template Name</Label>
                <Input value={editTemplate.name} onChange={e => setEditTemplate(p => ({...p, name: e.target.value}))}
                  className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="tpl-name" />
              </div>
              <div>
                <Label className="font-semibold">Subject</Label>
                <Input value={editTemplate.subject} onChange={e => setEditTemplate(p => ({...p, subject: e.target.value}))}
                  className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="tpl-subject" />
              </div>
              <div>
                <Label className="font-semibold">Body</Label>
                <Textarea value={editTemplate.body} onChange={e => setEditTemplate(p => ({...p, body: e.target.value}))}
                  rows={8} className="mt-1 rounded-xl border-[#E8E5DF] font-mono text-sm" data-testid="tpl-body" />
                <p className="text-xs text-[#6B6B7A] mt-1">Variables: {'{customer_name}'}, {'{child_name}'}, {'{party_date}'}, {'{party_time}'}, {'{package_name}'}, {'{balance_due}'}, {'{discount_code}'}</p>
              </div>
              <Button onClick={saveTemplate} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="save-tpl-btn">
                Save Template
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmCommunications;
